import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useConfigStore, useAppStore, useWindowStore } from '../stores';
import {
  getSettings,
  createContainerStyleMarkup,
  setDocumentStyleProperties,
  processFeatureFlags,
} from '../AppHelpers';
import { buildYourTourApiUrl, fetchYourTourApi } from '../utils/ApiHelpers';
import { applyRouteFavicon } from '../utils/favicon';
import getRouteDocumentTitle from '../utils/documentTitle';
import variables from '../GlobalScss/variables.scss';
import useFathomExclusion from './useFathomExclusion';
import useHeartbeat from './useHeartbeat';

const FATHOM_FILTERED_PLATFORMS = ['page', 'siteSee'];

/**
 * Custom hook to handle app settings, config, and initialization.
 * This includes:
 * - Parsing settings from URL/location
 * - Storing settings in app store
 * - Processing community config and feature flags
 * - Setting up container styles and CSS variables
 * - Window resize handling
 * - Page title updates
 * - Analytics script injection
 *
 * Community config is also kept live on platforms that display occupancy: the
 * heartbeat's communityConfig counter is bumped whenever the community node is
 * saved, and we refetch /your_tour_api/config/{slug} so feature flags, colors
 * and the page title update without a page reload. Elsewhere we do not poll at
 * all, and those changes land on the next page load.
 *
 * @param {Object} communityData - Community data from API
 * @returns {Object} Settings and container style
 */
const useAppSettings = (communityData) => {
  const [containerStyle, setContainerStyle] = useState(null);

  // Config refetched by the heartbeat, layered over the data App.js fetched.
  const [liveCommunityData, setLiveCommunityData] = useState(null);

  // Config store
  const config = useConfigStore((state) => state.config);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const setCommunityConfig = useConfigStore((state) => state.setCommunityConfig);

  // App store
  const setSettings = useAppStore((state) => state.setSettings);

  // Window store
  const setWindowDimensions = useWindowStore((state) => state.setWindowDimensions);

  const location = useLocation();
  const { pathname } = location;

  // Get settings from URL/location
  const settings = getSettings(window?.location?.host, location);

  const {
    platform,
    featurePlatform,
    isPublic,
    urlSlug,
    isProduction,
  } = settings;

  const { communityName } = config || {};

  // Turn off scrollbar if this isn't the PIB.
  useEffect(() => {
    if (platform !== 'page') {
      window.document.body.classList.add('noScroll');
    }
  }, [platform]);

  // Store settings in app store
  useEffect(() => {
    setSettings(settings);
  }, [settings, setSettings]);

  // A fresh fetch from App.js supersedes anything the heartbeat pulled in.
  const lastPropRef = useRef(communityData);
  if (lastPropRef.current !== communityData) {
    lastPropRef.current = communityData;
    if (liveCommunityData) {
      setLiveCommunityData(null);
    }
  }
  const effectiveCommunityData = liveCommunityData || communityData;

  // Heartbeat callback: the community node was saved, so pull fresh config.
  const refreshCommunityConfig = useCallback(async () => {
    if (!urlSlug) return;
    try {
      const url = buildYourTourApiUrl(`/your_tour_api/config/${urlSlug}`, {}, platform);
      const data = await fetchYourTourApi(url, platform);
      // Only replace config we can trust; a 403/error payload must not wipe it.
      if (data?.yt?.communityNid) {
        setLiveCommunityData(data);
      }
    } catch (error) {
      // Best-effort — the next poll or a page load will pick the change up.
      console.warn('Unable to refresh community config:', error);
    }
  }, [urlSlug, platform]);

  // Only poll once the initial config has landed (which implies we are authed),
  // and only where the platform displays occupancy. This is the same trade-off
  // useMapData already takes: on a platform that renders no occupancy this hook
  // would be the sole subscriber, so it would spin up a poller of its own purely
  // to notice admin-driven community-node edits. Feature flags, colors and the
  // page title are rare and land on the next page load there instead.
  const { platformShowOccupancy } = featureFlags || {};
  useHeartbeat(urlSlug, platform, {
    onCommunityConfigChange: refreshCommunityConfig,
    enabled: !!communityData && !!platformShowOccupancy,
  });

  // Set various config from community data.
  useEffect(() => {
    if (effectiveCommunityData) {
      const processedFeatureFlags = processFeatureFlags(
        featurePlatform,
        isPublic,
        effectiveCommunityData?.yt?.features,
      );

      setCommunityConfig(effectiveCommunityData, processedFeatureFlags);
    }

    // We want to use default colors if no colors are set.
    const primaryColor = effectiveCommunityData?.yt?.primaryColor || variables.primary;
    const secondaryColor = effectiveCommunityData?.yt?.secondaryColor || variables.secondar;

    setDocumentStyleProperties(
      window,
      primaryColor,
      secondaryColor,
    );

    const rawContainerStyle = createContainerStyleMarkup(
      primaryColor,
      secondaryColor,
    );

    setContainerStyle(rawContainerStyle);
  }, [featurePlatform, isPublic, effectiveCommunityData, setCommunityConfig]);

  // Listen for resize events and set window dims.
  useEffect(() => {
    setWindowDimensions(window.innerWidth, window.innerHeight);
    const handleResize = () => {
      setWindowDimensions(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setWindowDimensions]);

  // Update page title with community name, branded per route.
  useEffect(() => {
    const title = getRouteDocumentTitle(communityName, pathname, featurePlatform);
    if (title) {
      document.title = title;
    }
  }, [communityName, pathname, featurePlatform]);

  // Swap the favicon set per route: TourGuide on /tourguide, YourTour elsewhere.
  useEffect(() => {
    applyRouteFavicon(pathname);
  }, [pathname]);

  // PIB and /tourguide gate Fathom on an IP-based exclusion verdict from
  // Drupal so admin/sales-staff visits don't inflate per-PIB analytics.
  // Other platforms load Fathom unconditionally. The verdict resolves in
  // every env (so e2e can exercise it); only the script load is prod-gated.
  const isFathomFilteredPlatform = FATHOM_FILTERED_PLATFORMS.includes(platform);
  const { excluded: fathomExcluded, ready: fathomReady } = useFathomExclusion({
    enabled: isFathomFilteredPlatform,
    platform,
  });

  useEffect(() => {
    // ?fathomTest=1 forces Fathom to load in any env so the exclusion gate
    // can be verified on a dev server. Sticky for the tab via sessionStorage.
    const params = new URLSearchParams(window.location.search);
    if (params.get('fathomTest') === '1') {
      sessionStorage.setItem('ytFathomTestMode', '1');
    }
    const fathomForced = sessionStorage.getItem('ytFathomTestMode') === '1';

    if (!isProduction && !fathomForced) return undefined;
    if (isFathomFilteredPlatform && (!fathomReady || fathomExcluded)) return undefined;

    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.setAttribute('src', 'https://cdn.usefathom.com/script.js');
    script.setAttribute('data-spa', 'auto');
    script.setAttribute('data-site', 'ZLWPSRJO');
    script.setAttribute('defer', 'defer');
    head.appendChild(script);

    return () => {
      if (script.parentNode === head) {
        head.removeChild(script);
      }
    };
  }, [isProduction, isFathomFilteredPlatform, fathomReady, fathomExcluded]);

  return {
    settings,
    containerStyle,
    featureFlags,
    pathname,
  };
};

export default useAppSettings;
